import _ from 'lodash';
import React, {useContext, useEffect} from 'react';
import {useLocation} from 'react-router-dom';
import {DashboardBrokerUsage} from "../../types/DashboardBrokerUsage";
import {DashboardJobOrderCount} from "../../types/DashboardJobOrderCount";
import {DispatchOrder} from "../../types/DispatchOrder";
import {GlobalContext} from "../admin-frame/AdminFrame";
import {ApiUtility} from "../common/ApiUtility";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {Constants} from "../common/Constants";
import {DateControl} from "../common/DateControl";
import {DateUtility} from '../common/DateUtility';
import {useRenderableDataStore} from "../common/hooks/useRenderableDataStore";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {UrlUtility} from "../common/UrlUtility";
import {BrokerUsageBox} from "./BrokerUsageBox";
import {DispatchOrdersBox} from "./DispatchOrdersBox";
import {RecentDispatchOrdersList} from "./RecentDispatchOrdersList";

interface DashboardPageURLQueryParams {
    dateString?: string;
}

export const DashboardPage = function () {
    const context = useContext(GlobalContext);
    const urlBase = `/${Constants.routes.dashboard.pathSegment}`;
    const dataStore = useRenderableDataStore({
        dateString: DateUtility.jsDateToURLDate(new Date()),
        brokerUsages: [] as DashboardBrokerUsage[],
        brokerUsageLoading: true,
        brokerUsageExpanded: false,
        jobOrderCounts: [] as DashboardJobOrderCount[],
        jobOrdersLoading: true,
        dispatchOrdersExpanded: false,
        recentDispatchOrders: [] as DispatchOrder[],
        recentLoading: true,
        prevUrlQueryString: null,
    });
    const location = useLocation();

    // Update state if URL changes; the URL is the source of truth for the one date the whole page shows.
    if ((location && location.search) !== dataStore.data().prevUrlQueryString) {
        let queryParams: DashboardPageURLQueryParams = UrlUtility.getQueryParamsFromSearchString(location.search);

        let dateString = DateUtility.jsDateToURLDate(new Date());
        if (queryParams.dateString) {
            dateString = queryParams.dateString;
        }

        dataStore.updateAndRender({
            dateString: dateString,
            prevUrlQueryString: location.search,
        });
    }

    // Reload the broker-usage region whenever the page date changes; this region loads independently.
    const reloadBrokerUsageDebounced = _.debounce(function () {
        dataStore.updateAndRender({brokerUsageLoading: true});
        ApiUtility.getDashboardBrokerUsageForDate(dataStore.data().dateString)
            .then(function (brokerUsages: DashboardBrokerUsage[]) {
                dataStore.updateAndRender({brokerUsages: brokerUsages, brokerUsageLoading: false});
            })
            .catch(function (error: any) {
                dataStore.updateAndRender({brokerUsageLoading: false});
                console && console.error(error);
                MessageDisplayerUtility.error("An error occurred while loading broker usage.");
            });
    }, Constants.debounceTimeoutMs);

    useEffect(
        function () {
            reloadBrokerUsageDebounced();
            return function () {
                reloadBrokerUsageDebounced.cancel();
            };
        },
        ['' + dataStore.data().dateString]
    );

    // Reload the Dispatch Orders region whenever the page date changes; this region loads independently.
    const reloadJobOrderCountsDebounced = _.debounce(function () {
        dataStore.updateAndRender({jobOrdersLoading: true});
        ApiUtility.getDashboardJobOrderCountsForDate(dataStore.data().dateString)
            .then(function (jobOrderCounts: DashboardJobOrderCount[]) {
                dataStore.updateAndRender({jobOrderCounts: jobOrderCounts, jobOrdersLoading: false});
            })
            .catch(function (error: any) {
                dataStore.updateAndRender({jobOrdersLoading: false});
                console && console.error(error);
                MessageDisplayerUtility.error("An error occurred while loading dispatch order counts.");
            });
    }, Constants.debounceTimeoutMs);

    useEffect(
        function () {
            reloadJobOrderCountsDebounced();
            return function () {
                reloadJobOrderCountsDebounced.cancel();
            };
        },
        ['' + dataStore.data().dateString]
    );

    // Reload the Recent Dispatch Orders region whenever the page date changes; this region loads independently.
    const reloadRecentDispatchOrdersDebounced = _.debounce(function () {
        dataStore.updateAndRender({recentLoading: true});
        ApiUtility.getDispatchOrdersListForDate(dataStore.data().dateString, null, null, null, null, null)
            .then(function (dispatchOrdersListData: any) {
                dataStore.updateAndRender({
                    recentDispatchOrders: dispatchOrdersListData.dispatchOrdersList,
                    recentLoading: false,
                });
            })
            .catch(function (error: any) {
                dataStore.updateAndRender({recentLoading: false});
                console && console.error(error);
                MessageDisplayerUtility.error("An error occurred while loading recent dispatch orders.");
            });
    }, Constants.debounceTimeoutMs);

    useEffect(
        function () {
            reloadRecentDispatchOrdersDebounced();
            return function () {
                reloadRecentDispatchOrdersDebounced.cancel();
            };
        },
        ['' + dataStore.data().dateString]
    );

    function handleFilterDateChange(date: Date | null) {
        if (date) {
            let selectedDateString = DateUtility.jsDateToURLDate(date);
            if (dataStore.data().dateString !== selectedDateString) {
                let currentQueryParams: any = UrlUtility.getQueryParamsFromSearchString(location.search);
                let updatedQueryParams: any = {...currentQueryParams};
                updatedQueryParams.dateString = selectedDateString;
                let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(updatedQueryParams);
                context.navigator.navigateTo(`${urlBase}${queryString}`);
            }
        }
    }

    function handlePreviousDate() {
        let previousDateString = DateUtility.jsDateToURLDate(
            DateUtility.urlDateToMoment(dataStore.data().dateString).subtract(1, 'days').toDate()
        );
        let currentQueryParams: any = UrlUtility.getQueryParamsFromSearchString(location.search);
        let updatedQueryParams: any = {...currentQueryParams};
        updatedQueryParams.dateString = previousDateString;
        let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(updatedQueryParams);
        context.navigator.navigateTo(`${urlBase}${queryString}`);
    }

    function handleNextDate() {
        let nextDateString = DateUtility.jsDateToURLDate(
            DateUtility.urlDateToMoment(dataStore.data().dateString).add(1, 'days').toDate()
        );
        let currentQueryParams: any = UrlUtility.getQueryParamsFromSearchString(location.search);
        let updatedQueryParams: any = {...currentQueryParams};
        updatedQueryParams.dateString = nextDateString;
        let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(updatedQueryParams);
        context.navigator.navigateTo(`${urlBase}${queryString}`);
    }

    function handleBrokerUsageToggleExpanded(expanded: boolean) {
        dataStore.updateAndRender({brokerUsageExpanded: expanded});
    }

    function handleDispatchOrdersToggleExpanded(expanded: boolean) {
        dataStore.updateAndRender({dispatchOrdersExpanded: expanded});
    }

    function handleClickRecentDispatchOrder(dispatchOrderId: number) {
        context.navigator.navigateTo('/' + Constants.routes.dispatch.pathSegment + '/dispatch-orders/' + dispatchOrderId);
    }

    function handleClickRecentViewAll() {
        context.navigator.navigateTo('/' + Constants.routes.dispatch.pathSegment);
    }

    return (
        <div className="dashboard-page">
            <div className="dashboard-page-content">
                <DateControl
                    dateString={dataStore.data().dateString}
                    onPreviousDate={handlePreviousDate}
                    onNextDate={handleNextDate}
                    onDateChange={handleFilterDateChange}
                />
                <div className="dashboard-top-row">
                    <ConditionalRenderer
                        if={dataStore.data().brokerUsageLoading}
                        renderer={() => <OverlayLoadingIndicator/>}
                    />
                    <BrokerUsageBox
                        brokerUsages={dataStore.data().brokerUsages}
                        expanded={dataStore.data().brokerUsageExpanded}
                        onToggleExpanded={handleBrokerUsageToggleExpanded}
                    />
                    <ConditionalRenderer
                        if={dataStore.data().jobOrdersLoading}
                        renderer={() => <OverlayLoadingIndicator/>}
                    />
                    <DispatchOrdersBox
                        jobOrderCounts={dataStore.data().jobOrderCounts}
                        expanded={dataStore.data().dispatchOrdersExpanded}
                        onToggleExpanded={handleDispatchOrdersToggleExpanded}
                    />
                </div>
                <div className="dashboard-recent-section">
                    <ConditionalRenderer
                        if={dataStore.data().recentLoading}
                        renderer={() => <OverlayLoadingIndicator/>}
                    />
                    <RecentDispatchOrdersList
                        dispatchOrders={dataStore.data().recentDispatchOrders}
                        onClickRow={handleClickRecentDispatchOrder}
                        onClickViewAll={handleClickRecentViewAll}
                    />
                </div>
            </div>
        </div>
    );
};
